import { useRef, type KeyboardEvent } from 'react';
import { Button, Card, Form, Input, Message, Typography } from '@arco-design/web-react';
import type { RefInputType } from '@arco-design/web-react/es/Input/interface';
import { IconLock, IconUser } from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import type { LoginRequest } from '@/types/user';

export default function LoginView() {
  const navigate = useNavigate();
  const { login, loading } = useAuthStore();
  const [form] = Form.useForm<LoginRequest>();
  const passwordRef = useRef<RefInputType>(null);

  const onSubmit = async () => {
    if (loading) return;
    try {
      const values = await form.validate();
      await login(values);
      Message.success('登录成功');
      navigate('/dashboard');
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const focusPassword = () => {
    passwordRef.current?.focus();
  };

  const handleUsernameKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      focusPassword();
      return;
    }
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      focusPassword();
    }
  };

  const handlePasswordKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f1f48 0%, #165DFF 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 'min(520px, 100%)',
          borderRadius: 16,
          boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
        }}
        bodyStyle={{ padding: '40px 48px 48px' }}
      >
        <Typography.Title heading={3} style={{ marginTop: 0, marginBottom: 8 }}>
          UP·CARD 控制台
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 15 }}>
          一站式资产管理 / 风控对账平台
        </Typography.Text>
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 32 }}
          onSubmit={onSubmit}
          size="large"
        >
          <Form.Item field="username" rules={[{ required: true, message: '请输入登录账号' }]}>
            <Input
              prefix={<IconUser />}
              placeholder="登录账号"
              allowClear
              autoFocus
              onKeyDown={handleUsernameKeyDown}
            />
          </Form.Item>
          <Form.Item field="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password
              ref={passwordRef}
              prefix={<IconLock />}
              placeholder="登录密码"
              onKeyDown={handlePasswordKeyDown}
            />
          </Form.Item>
          <Button type="primary" long loading={loading} htmlType="submit" style={{ height: 44 }}>
            登录
          </Button>
        </Form>
      </Card>
    </div>
  );
}
